// One-button two-phase domain audit (sweep → deep) — client API helpers and
// the HTML report renderer. The renderer is a PURE function of the structured
// report returned by GET /api/lab-audits/domain-audit/:id/report — no
// measurement logic lives here.

import { secureFetch } from '../services/api.js';
import { downloadHTMLFile } from './diagnosticsExportStyles.js';

export { downloadHTMLFile };

export const startDomainAudit = async (url) => {
  const res = await secureFetch('/api/lab-audits/domain-audit', {
    method: 'POST',
    body: JSON.stringify({ url })
  });
  const json = await res.json().catch(() => ({}));
  if (res.status === 409 && json.auditId) return { auditId: json.auditId, resumed: true };
  if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`);
  return { auditId: json.auditId, resumed: false };
};

export const getDomainAuditStatus = async (auditId) => {
  const res = await secureFetch(`/api/lab-audits/domain-audit/${auditId}/status`);
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`);
  return json.status;
};

export const cancelDomainAudit = async (auditId) => {
  const res = await secureFetch(`/api/lab-audits/domain-audit/${auditId}/cancel`, { method: 'POST' });
  return res.ok;
};

export const getDomainAuditReport = async (auditId) => {
  const res = await secureFetch(`/api/lab-audits/domain-audit/${auditId}/report`);
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`);
  return json.report;
};

// ---------------------------------------------------------------------------
// Report rendering (pure — consumes only the stored-runs report)
// ---------------------------------------------------------------------------
const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const fmtVal = (key, v) => {
  if (v == null) return '—';
  if (key === 'cls') return Number(v).toFixed(3);
  if (v >= 1000) return `${(v / 1000).toFixed(2)} s`;
  return `${Math.round(v)} ms`;
};

const statusOf = (key, v, thresholds) => {
  const t = thresholds?.[key];
  if (!t || v == null) return 'na';
  if (v <= t.good) return 'good';
  if (v <= t.poor) return 'warn';
  return 'poor';
};

const STATUS_COLOR = { good: '#22c55e', warn: '#f59e0b', poor: '#ef4444', na: '#64748b' };

const shortUrl = (url, max = 55) => {
  let s = String(url || '').replace(/^https?:\/\//, '');
  if (s.length > max) s = s.slice(0, max - 1) + '…';
  return s;
};

const cellHtml = (key, agg, runsTotal, thresholds, cs) => {
  const m = agg?.metrics?.[key];
  if (!m || m.measured === 0) {
    return `<td class="na">${cs ? 'nezměřeno' : 'not measured'}</td>`;
  }
  const st = statusOf(key, m.median, thresholds);
  const spread = (m.min != null && m.max != null && m.max !== m.min)
    ? `<div class="spread">${fmtVal(key, m.min)} – ${fmtVal(key, m.max)}</div>` : '';
  const partial = m.measured < runsTotal
    ? `<div class="partial">${m.measured}/${runsTotal} ${cs ? 'běhů' : 'runs'}</div>` : '';
  return `<td><span class="val" style="color:${STATUS_COLOR[st]}">${fmtVal(key, m.median)}</span>${spread}${partial}</td>`;
};

export const generateDomainAuditHTML = (report, language = 'cs') => {
  const cs = language === 'cs';
  const t = report.thresholds || {};
  const d = report.disclosure || {};
  const domain = report.audit?.domain || '';
  const deviceLabel = (dev) => dev === 'desktop' ? 'Desktop' : (cs ? 'Mobil' : 'Mobile');
  const cmpLabel = (mode) => mode === 'accept'
    ? (cs ? 'Souhlas udělen' : 'Consent given')
    : (cs ? 'Bez interakce s lištou' : 'Banner untouched');

  const dateStr = report.audit?.completedAt
    ? new Date(report.audit.completedAt).toLocaleString(cs ? 'cs-CZ' : 'en-US')
    : new Date().toLocaleString(cs ? 'cs-CZ' : 'en-US');

  // --- Sweep table ---
  const worstSet = new Set(report.sweep?.worstPages || []);
  const sweepRows = (report.sweep?.pages || []).map(p => {
    if (!p.success) {
      return `<tr class="failed"><td>${esc(shortUrl(p.url))}</td><td colspan="3">${cs ? 'Běh selhal' : 'Run failed'}${p.error ? ` — ${esc(String(p.error).slice(0, 80))}` : ''}</td><td>—</td></tr>`;
    }
    const m = p.metrics || {};
    const cell = (key) => {
      const st = statusOf(key, m[key], t);
      return `<td><span class="val" style="color:${STATUS_COLOR[st]}">${fmtVal(key, m[key])}</span></td>`;
    };
    return `<tr${worstSet.has(p.url) ? ' class="worst"' : ''}>
      <td>${esc(shortUrl(p.url))}${worstSet.has(p.url) ? `<span class="worst-badge">${cs ? 'hloubkový test' : 'deep test'}</span>` : ''}</td>
      ${cell('lcp')}${cell('cls')}${cell('ttfb')}
      <td>${p.cmpClicked ? '✓' : '—'}</td>
    </tr>`;
  }).join('');

  // --- Deep matrix sections per worst page ---
  const deepSections = (report.deep?.pages || []).map(page => {
    const rows = (page.cells || []).map(cell => {
      const failNote = cell.runsFailed > 0
        ? `<div class="partial">${cs
            ? `${cell.runsFailed} z ${cell.runsTotal} běhů selhalo`
            : `${cell.runsFailed} of ${cell.runsTotal} runs failed`}</div>` : '';
      return `<tr>
        <td>${deviceLabel(cell.device)}</td>
        <td>${cmpLabel(cell.cmpMode)}${cell.cmpMode === 'accept' && cell.cmpClickedCount === 0 ? `<div class="partial">${cs ? 'lišta nenalezena' : 'banner not found'}</div>` : ''}</td>
        ${cellHtml('lcp', cell, cell.runsTotal, t, cs)}
        ${cellHtml('inp', cell, cell.runsTotal, t, cs)}
        ${cellHtml('cls', cell, cell.runsTotal, t, cs)}
        ${cellHtml('ttfb', cell, cell.runsTotal, t, cs)}
        <td>${cell.runsTotal - cell.runsFailed}/${cell.runsTotal}${failNote}</td>
      </tr>`;
    }).join('');
    return `
    <div class="card">
      <div class="section-label">${cs ? 'Hloubková matice' : 'Deep matrix'}</div>
      <h3>${esc(shortUrl(page.url, 80))}</h3>
      <p class="hint">${cs
        ? 'Medián z opakovaných běhů; rozpětí min–max ukazuje rozptyl mezi běhy.'
        : 'Median of repeated runs; the min–max range shows spread between runs.'}</p>
      <table>
        <thead><tr>
          <th>${cs ? 'Zařízení' : 'Device'}</th><th>${cs ? 'Cookie lišta' : 'Cookie banner'}</th>
          <th>LCP</th><th>INP</th><th>CLS</th><th>TTFB</th>
          <th>${cs ? 'Úspěšné běhy' : 'Successful runs'}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
  }).join('');

  const unmeasuredNote = (d.failedRuns > 0 || d.inpUnmeasured > 0) ? `
    <div class="callout">
      <strong>${cs ? 'Přiznané díry v datech:' : 'Disclosed data gaps:'}</strong>
      ${d.failedRuns > 0 ? (cs
        ? ` ${d.failedRuns} z ${d.totalRuns} běhů se nepodařilo změřit.`
        : ` ${d.failedRuns} of ${d.totalRuns} runs could not be measured.`) : ''}
      ${d.inpUnmeasured > 0 ? (cs
        ? ` U ${d.inpUnmeasured} z ${d.inpAttempted} hloubkových běhů se nepodařilo změřit interakci (INP).`
        : ` In ${d.inpUnmeasured} of ${d.inpAttempted} deep runs the interaction (INP) was not measured.`) : ''}
      ${cs
        ? ' Hodnoty v reportu vycházejí pouze ze skutečně naměřených běhů.'
        : ' Values in this report come only from actually measured runs.'}
    </div>` : '';

  const title = cs ? `Kompletní audit domény — ${domain}` : `Full domain audit — ${domain}`;

  return `<!DOCTYPE html>
<html lang="${cs ? 'cs' : 'en'}">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(title)}</title>
<style>
  :root { color-scheme: dark; }
  * { box-sizing: border-box; }
  body { margin: 0; padding: 32px 20px; background: #0f172a; color: #e2e8f0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
  .wrap { max-width: 960px; margin: 0 auto; }
  h1 { font-size: 24px; margin: 0 0 4px; color: #fff; }
  h3 { font-size: 16px; margin: 4px 0 4px; color: #fff; word-break: break-all; }
  .meta { color: #94a3b8; font-size: 13px; margin-bottom: 24px; }
  .card { background: #1e293b; border: 1px solid #334155; border-radius: 14px;
    padding: 20px; margin-bottom: 20px; }
  .section-label { font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
    text-transform: uppercase; color: #38bdf8; margin-bottom: 4px; }
  .hint { color: #94a3b8; font-size: 12px; margin: 0 0 12px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th { text-align: left; color: #94a3b8; font-weight: 600; font-size: 11px;
    text-transform: uppercase; letter-spacing: 0.04em; padding: 8px 10px;
    border-bottom: 1px solid #334155; }
  td { padding: 9px 10px; border-bottom: 1px solid #263449; vertical-align: top; }
  tr:last-child td { border-bottom: none; }
  tr.worst { background: rgba(239, 68, 68, 0.07); }
  tr.failed td { color: #94a3b8; font-style: italic; }
  .val { font-weight: 700; }
  .spread { color: #94a3b8; font-size: 11px; margin-top: 2px; }
  .partial { color: #f59e0b; font-size: 11px; margin-top: 2px; }
  .na { color: #64748b; font-style: italic; }
  .worst-badge { display: inline-block; margin-left: 8px; padding: 1px 8px;
    border-radius: 999px; font-size: 10px; font-weight: 700; background: rgba(239,68,68,0.15);
    color: #f87171; border: 1px solid rgba(239,68,68,0.4); }
  .callout { background: rgba(245, 158, 11, 0.08); border: 1px solid rgba(245, 158, 11, 0.35);
    border-radius: 12px; padding: 14px 16px; font-size: 13px; margin-bottom: 20px; color: #fbbf24; }
  .summary-grid { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 20px; }
  .summary-item { flex: 1 1 140px; background: #1e293b; border: 1px solid #334155;
    border-radius: 12px; padding: 12px 14px; }
  .summary-item .k { font-size: 11px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; }
  .summary-item .v { font-size: 18px; font-weight: 700; color: #fff; margin-top: 2px; }
  .footer { color: #64748b; font-size: 11px; margin-top: 28px; text-align: center; }
</style>
</head>
<body>
<div class="wrap">
  <h1>${esc(title)}</h1>
  <div class="meta">${esc(report.audit?.baseUrl || '')} · ${esc(dateStr)} · ${cs
    ? `převažující zařízení: ${deviceLabel(report.audit?.dominantDevice)} (${report.audit?.deviceSource === 'crux' ? 'dle CrUX' : cs ? 'výchozí' : 'default'})`
    : `dominant device: ${deviceLabel(report.audit?.dominantDevice)} (${report.audit?.deviceSource === 'crux' ? 'per CrUX' : 'default'})`}</div>

  <div class="summary-grid">
    <div class="summary-item"><div class="k">${cs ? 'Stránek ve sweepu' : 'Pages swept'}</div><div class="v">${(report.sweep?.pages || []).length}</div></div>
    <div class="summary-item"><div class="k">${cs ? 'Hloubkově testováno' : 'Deep-tested'}</div><div class="v">${(report.deep?.pages || []).length}</div></div>
    <div class="summary-item"><div class="k">${cs ? 'Běhů celkem' : 'Total runs'}</div><div class="v">${d.totalRuns ?? '—'}</div></div>
    <div class="summary-item"><div class="k">${cs ? 'Nezměřené běhy' : 'Unmeasured runs'}</div><div class="v">${d.failedRuns ?? 0}</div></div>
  </div>

  ${unmeasuredNote}

  <div class="card">
    <div class="section-label">${cs ? 'Fáze 1 — Sweep napříč stránkami' : 'Phase 1 — Cross-page sweep'}</div>
    <h3>${cs ? 'Rychlé měření (1 běh na stránku)' : 'Quick measurement (1 run per page)'}</h3>
    <p class="hint">${cs
      ? `Každá stránka změřena jednou na zařízení „${deviceLabel(report.audit?.dominantDevice)}" s udělením souhlasu na cookie liště. Nejhorší stránky postoupily do hloubkové fáze.`
      : `Each page measured once on "${deviceLabel(report.audit?.dominantDevice)}" with cookie consent given. The worst pages advanced to the deep phase.`}</p>
    <table>
      <thead><tr><th>${cs ? 'Stránka' : 'Page'}</th><th>LCP</th><th>CLS</th><th>TTFB</th><th>CMP</th></tr></thead>
      <tbody>${sweepRows}</tbody>
    </table>
  </div>

  ${deepSections}

  <div class="footer">ControlVitals — ${cs
    ? 'Report vygenerován výhradně z uložených výsledků jednotlivých běhů.'
    : 'Report generated exclusively from stored per-run results.'}</div>
</div>
</body>
</html>`;
};

export const buildDomainAuditFilename = (domain) =>
  `domain_audit_${String(domain || 'report').replace(/\./g, '_')}_${new Date().toISOString().split('T')[0]}.html`;
